var Layout    = require("enyo-ui/Layout"),
    kind      = require("enyo/kind"),
    themeVars = require("app/theme/variables.js");

/**
 * This layout is meant to aid in positioning dropdowns displayed within the
 * application's main UI. Styles are applied to the dropdown component of the
 * container, which is expected to be a non-floating Popup.
 */
module.exports = kind(/** @lends app.AppLayout.DropdownLayout.prototype */ {

    /**
     * @private
     */
    name: "app.AppLayout.DropdownLayout",

    /**
     * @private
     */
    kind: Layout,

    /**
     * Default options for the layout.
     *
     * @type {Object}
     * @protected
     */
    options: /** @lends  app.AppLayout.DropdownLayout.prototype */ {

        /**
         * The dropdown will be scaled relative to a particular part of the app
         * layout.
         *
         * Current options include:
         * - `contentRegion`  Sets the maximum height of the dropdown to the
         *                    height of the `contentRegion` of the app layout.
         *                    Scroller height gets limited to the height of the
         *                    `contentRegion` as well.
         * - `fullLayout`     Sets the maximum height of the dropdown to the
         *                    height of the entire app layout. Scroller height
         *                    gets limited to the height of the entire app
         *                    layout as well.
         *
         * @type     {string|function}
         * @default  'contentRegion'
         * @public
         */
        alignment: "contentRegion",

        /**
         * The path from the dropdown to the scroller. If a path is set and a
         * scroller is found, the max height of the scroller gets set to the
         * same maxHeight as the dropdown itself.
         *
         * If the height of the scroller needs to be different, you can override
         * the `calcScrollerMaxHeight()` method.
         *
         * @type     {string|function|null}
         * @default  null
         * @public
         */
        scrollerPath: null
    },

    /**
     * @public
     */
    reflow: function() {
        var container = this.container,
            dropdown  = container.$.dropdown,
            appView   = container.app.view,
            maxHeight,
            scroller,
            scrollerMaxHeight;

        if (!appView.hasNode() || !container.hasNode()) {
            return;
        }

        // Help with setting the max-height of the dropdown's main scroller
        scroller = this.getScroller();

        if (scroller) {
            scrollerMaxHeight = this.calcScrollerMaxHeight();
            scroller.set("maxHeight", scrollerMaxHeight + "px");
        }

        // Set maxHeigh of the dropdown
        maxHeight = this.calcMaxHeight() || null;
        dropdown.applyStyle("max-height", maxHeight + "px");
    },

    /**
     * By default we use the scrollerPath option to resolve the scroller.
     * Override this method if another strategy would be better.
     *
     * @returns  {enyo.Control}  The scroller
     * @protected
     */
    getScroller: function() {
        var scrollerPath,
            scroller;

        scrollerPath = this.getOption("scrollerPath") || null;

        if (scrollerPath) {
            scroller = this.container.get(scrollerPath);
        }

        return scroller || null;
    },

    /**
     * @returns  {number}  The max height of the control
     * @protected
     */
    calcMaxHeight: function() {
        var appView             = this.container.app.view,
            headerRegionBounds  = appView.get("headerRegionBounds") || {},
            contentRegionBounds = appView.get("contentRegionBounds") || {},
            headerRegionHeight  = headerRegionBounds.height || 0,
            contentRegionHeight = contentRegionBounds.height || 0,
            maxHeight           = 0;

        // Max-height starts out covering part of the layout
        switch (this.getOption("alignment")) {
            case "fullLayout":
                maxHeight = contentRegionHeight;

                if (themeVars.appHeaderFloat === false) {
                    maxHeight += headerRegionHeight;
                }

                maxHeight -= themeVars.safeAreaInsetTop;
                maxHeight -= themeVars.safeAreaInsetBottom;

                return maxHeight;
            case "contentRegion":
            default:
                maxHeight = contentRegionHeight;

                if (themeVars.appHeaderFloat === true) {
                    maxHeight -= headerRegionHeight;
                }

                maxHeight -= themeVars.safeAreaInsetBottom;

                return maxHeight;
        }
    },

    /**
     * @returns  {number}  The max height of the scroller
     * @protected
     */
    calcScrollerMaxHeight: function() {
        return this.calcMaxHeight() || 0;
    }

});
